// @flow
import * as React from 'react';
import { QuizStepNumber, QuizStepHeading } from '../../app/features';
import { getCId, getDisclamers, getStoredUser, setClientId, setStoredUser } from '@coverright/utils';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createLoginUrl, keycloak, KeycloakContext } from '@coverright/shared/keycloak';
import { ProfileSource } from '@coverright/data-access/types/enrollment';
import { CreateAccountForm } from '@coverright/widgets/create-account-form';
import { environment } from '../../../environments/environment';
import {
  useMaPlansCount,
  useSaveMedicareQuoteMutation,
} from '@coverright/data-access/medicare';
import { getRoute } from '../../app/router';
import { getFeatureFlagVariant, keycloakReady } from '../../../init-app';
import { PersonalInfoLoading } from './personal-info-loading';
import { EXP_POST_SIGNUP_Z1_REDIRECT, TickIcon } from '../../shared';
import { LoadingButton } from '@mui/lab';
import { MedicareQuoteFilterExtraBenefit } from '@coverright/data-access/types/medicare';
import { InfoPanel } from '@coverright/features/info-panel';

// framer-motion's AnimatePresence returns Element|undefined which conflicts with React's
// JSX element constraint (Element|null) — cast to a compatible type
const AP = AnimatePresence as React.ComponentType<{
  initial?: boolean;
  mode?: 'wait' | 'sync' | 'popLayout';
  children?: React.ReactNode;
}>;

const TRUST_LOGOS = [
  'assets/img/trust-logos/USAToday.png',
  'assets/img/trust-logos/Nerdwallet.png',
  'assets/img/trust-logos/MoneyWise.png',
];

export const PersonalInfo = () => {
  const timeout = useRef<NodeJS.Timeout | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { getHash } = React.useContext(KeycloakContext);
  const nextRoute = getRoute('analyzingInfo');
  const { maPlansCount, maOrgCount } = useMaPlansCount(getStoredUser()?.zip);
  const [loading, setLoading] = React.useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);
  const [saveQuote] = useSaveMedicareQuoteMutation();
  const [activeTrustLogo, setActiveTrustLogo] = useState(0);
  const [showAnalyzing, setShowAnalyzing] = useState(false);
  const [analyzingDone, setAnalyzingDone] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);

  const source = sessionStorage.getItem('utm_source');

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTrustLogo((i) => (i + 1) % TRUST_LOGOS.length);
    }, 2000);
    return () => clearInterval(timer);
  }, []);


  useEffect(() => {
    let cancelled = false;
    // keycloak init now runs in the background on this page, so wait for it
    // to settle before trusting keycloak.authenticated. Swallow rejection
    // (e.g. admin declined logout) instead of leaving it unhandled — we
    // still want to run the check below either way.
    keycloakReady.catch(() => undefined).then(() => {
      if (cancelled) return;
      if (keycloak.authenticated) {
        navigate(nextRoute);
      } else {
        const {zip, countyName} = (getStoredUser() || {})
        if (!zip || !countyName) {
          navigate(getRoute('zip'));
        }
      }
    });
    return () => { cancelled = true; };
  }, [navigate, nextRoute])

  const attributes = useMemo(() => {
    const result = [];
    if (source && source !== 'test') {
      result.push({ key: 'source', value: source === 'sql25q1' ? 'compare' : source })
    }

    const partnertest = sessionStorage.getItem('partnertest');
    if (partnertest) {
      result.push({ key: 'test_lead', value: 'true' })
    }

    return result.length ? result : undefined;
  }, [source])

  const onSuccess = async () => {
    if (keycloak?.authenticated) {
      const variant = await getFeatureFlagVariant(EXP_POST_SIGNUP_Z1_REDIRECT);
      if (variant === 'test') {
        window.location.href = `${environment.planFinderZ1Url.replace(/\/$/, '')}/plans/ma` + getHash();
        return;
      }
      setAccountCreated(true);
    }
  };

  useEffect(() => {
    if (analyzingDone && accountCreated) {
      navigate(nextRoute);
    }
  }, [analyzingDone, accountCreated, navigate, nextRoute]);

  const handleSubmitClick = async () => {
    const {zip, countyName} = (getStoredUser() || {})
    if (formRef.current && zip && countyName) {
      setLoading(true);
      await saveQuote({
        variables: {
          data: {
            cId: getCId(),
            zip,
            county: countyName,
            filters: {
              extraBenefits: [MedicareQuoteFilterExtraBenefit.DrugCoverage],
            },
          },
        },
      })
        .then((res) => {
          setStoredUser({...getStoredUser(), quoteId: res.data?.saveMedicareQuote.maQuoteID});
          setClientId(res.data?.saveMedicareQuote.clientId)
        })
        .finally(() => setLoading(false));

      // requestSubmit runs form validation; the analyzing screen is only
      // revealed once the form passes validation (via onSubmitStart below),
      // so an incomplete form can never progress past this step.
      formRef.current.requestSubmit();
    }
  }

  // Arrived here straight from /spotower after the silent signup hit an
  // already-existing account — the form is already fully prefilled, so
  // re-submit immediately and let the existing "Account already exists" flow
  // below surface its dialog, instead of making the visitor click Continue
  // just to see the same error again.
  useEffect(() => {
    if (sessionStorage.getItem('spotowerRetry')) {
      sessionStorage.removeItem('spotowerRetry');
      handleSubmitClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitStart = () => {
    timeout.current = setTimeout(() => {
      setShowAnalyzing(true)
      timeout.current = null;
    }, 4000);
  }


  return (
    <>
      {showAnalyzing && (
        <PersonalInfoLoading onComplete={() => setAnalyzingDone(true)} />
      )}

      {/* CreateAccountForm must stay mounted while the loading screen is visible —
          its onSuccess callback drives the transition to the result page */}
      <Box sx={{ display: showAnalyzing ? 'none' : 'block' }}>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} mb={4}>
        <QuizStepNumber step={3} />

        <Stack component={motion.div}
               initial="hidden"
               animate="visible"
               variants={{
                 hidden: {},
                 visible: { transition: { staggerChildren: 0.1, delayChildren: .5 } },
               }}

               direction={'row'} alignItems={'center'}>
          <motion.div
            variants={{
              hidden: { opacity: 0, x: 10 },
              visible: { opacity: 1, x: 0, transition: { duration: 0.3, ease: 'easeOut' } },
            }}
          >
            <Typography variant={'h4'} fontWeight={700} mr={2}>
              Trusted by
            </Typography>
          </motion.div>
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
            {TRUST_LOGOS.map((src) => <TrustLogo key={src} src={src} />)}
          </Box>

          <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', height: 32 }}>
            <AP mode="wait">
              <motion.img
                key={activeTrustLogo}
                src={TRUST_LOGOS[activeTrustLogo]}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                style={{ maxHeight: 32 }}
              />
            </AP>
          </Box>
        </Stack>
      </Stack>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
      >
        <QuizStepHeading
          variant={'h1'}
          sx={{ mb: { xs: 1, sm: 2 } }}
          tabIndex={-1}
        >
          How can we reach you?
        </QuizStepHeading>

        <CreateAccountForm
          sx={{ '& div.MuiGrid-item': { pt: 3.5 } }}
          onLoadingChange={setLoading}
          columns={1.5}
          ref={formRef}
          inputProps={{
            firstName: {
              tickIcon: <TickIcon />,
              inputProps: { 'data-test': 'first_name' },
            },
            lastName: {
              tickIcon: <TickIcon />,
              inputProps: { 'data-test': 'last_name' },
            },
            email: {
              tickIcon: <TickIcon />,
              inputProps: { 'data-test': 'email' },
            },
            phoneNumber: {
              tickIcon: <TickIcon />,
              inputProps: { 'data-test': 'phone-number-input' },
            },
          }}
          onSuccess={onSuccess}
          onSubmitStart={onSubmitStart}
          signInLink={createLoginUrl(environment.planFinderZ1Url)}
          source={ProfileSource.ApplicationOnboarding}
          attributes={attributes}
          onError={err => {
            setShowAnalyzing(false)
            clearTimeout(timeout.current || undefined);
            timeout.current = null;
            console.error(err)
          }}
          showPhone
        />


        <LoadingButton ref={buttonRef} variant={'contained'}
                       loading={loading}
                sx={{mt: {xs: 4, sm: 2}, mb: 1, fontWeight: 600, alignItems: 'center'}}
                fullWidth
                       onClick={handleSubmitClick}
                       loadingIndicator={<>
                         <CircularProgress size={18} sx={{color: '#FFF', mr: 1}} thickness={6} />
                         <Typography component={'span'} color={'#FFF'}
                                     lineHeight={1}
                                     fontSize={18}
                                     fontWeight={600}>Submitting</Typography>
                        </>}
                id={'continue-button'}>
          Continue
        </LoadingButton>

        <InfoPanel
          sx={{mt: 3}}
          title={'We never sell your info'}
          text={
            'We never sell your information or personal details to third parties. Your information is stored on HIPAA-compliant data storage with 256-bit encryption.'
          }
        />


        <Divider sx={{mt: 4, mb: 3}} />

        <Box
          sx={{
            border: '2px solid #cad5e2',
            bgcolor: '#FFF',
            p: 2,
            borderRadius: 1,
            overflowY: 'auto',
            height: 125,
          }}
        >
          <Typography fontWeight={500} color={'text.secondary'}>
            {getDisclamers('PERSONAL_INFO_ONBOARDING', {
              maPlansCount,
              maOrgCount,
            }, 'Continue')}
          </Typography>
        </Box>
      </motion.div>
      </Box>
    </>
  );
};

const TrustLogo = styled((props: any) => (
  <motion.img variants={{
    hidden: { opacity: 0, x: 10 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.3, ease: 'easeOut' } },
  }}
              {...props} />
))({
  maxHeight: 32,
})
